import { queryOptions, skipToken } from '@tanstack/react-query';

import { EntryTypeBlockData, EntryTypeListItemData } from 'chaika-frontend-types/frontend-cms';

import { BlockApi } from '@api/BlockApi';
import { EntryTypeApi } from '@api/EntryTypeApi';

import { EntryTypeDraft } from '../types';
import { collectActiveBlocks } from '../utils/activeBlocks';
import { normalizeEntryTypeDraft } from '../utils/entryTypeDraftData';

const MINUTE_MS = 60_000;
const SERVICE_STALE_TIME_MS = MINUTE_MS;
const BLOCK_STALE_TIME_MS = 5 * MINUTE_MS;
const ENTRY_TYPE_STALE_TIME_MS = 5 * MINUTE_MS;
const SERVICE_LIST_LIMIT = 5000;

export const editEntryTypeKeys = {
    all: ['editEntryType'] as const,
    details: () => [...editEntryTypeKeys.all, 'detail'] as const,
    detail: (entryTypeId: string, version: number) => [...editEntryTypeKeys.details(), entryTypeId, version] as const,
    services: (entryTypeId: string, version: number) =>
        [...editEntryTypeKeys.all, 'service', entryTypeId, version] as const,
    activeBlocks: () => [...editEntryTypeKeys.all, 'activeBlock'] as const,
    entryTypes: () => [...editEntryTypeKeys.all, 'entryType'] as const,
};

export const editEntryTypeQueryOptions = {
    detail: (entryTypeId: string, version: number) =>
        queryOptions({
            staleTime: Infinity,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            queryKey: editEntryTypeKeys.detail(entryTypeId, version),
            queryFn: entryTypeId
                ? async (): Promise<EntryTypeDraft> => {
                      const entryType = await EntryTypeApi.getVersionWithTranslation(entryTypeId, version);

                      return normalizeEntryTypeDraft(entryType);
                  }
                : skipToken,
        }),
    services: (entryTypeId: string, version: number) =>
        queryOptions({
            staleTime: SERVICE_STALE_TIME_MS,
            queryKey: editEntryTypeKeys.services(entryTypeId, version),
            queryFn: async (): Promise<string[]> => {
                const { serviceIds } = await EntryTypeApi.getServices(entryTypeId, version, {
                    limit: SERVICE_LIST_LIMIT,
                    offset: 0,
                });

                return serviceIds;
            },
        }),
    activeBlocks: () =>
        queryOptions({
            staleTime: BLOCK_STALE_TIME_MS,
            queryKey: editEntryTypeKeys.activeBlocks(),
            queryFn: async (): Promise<EntryTypeBlockData[]> => collectActiveBlocks(await BlockApi.getList()),
        }),
    entryTypes: () =>
        queryOptions({
            staleTime: ENTRY_TYPE_STALE_TIME_MS,
            queryKey: editEntryTypeKeys.entryTypes(),
            queryFn: async (): Promise<EntryTypeListItemData[]> => {
                const { entryTypes } = await EntryTypeApi.getList();

                return entryTypes;
            },
        }),
};
